import { Box, Paper, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink, Outlet, useLocation, useParams } from "react-router-dom";
import PatientConditionTimeline from "../components/PatientConditionTimeline";
import { useToast } from "../context/ToastContext";
import usePermissions from "../utils/permissions";
import PatientInfo from "../commons/PatientInfo";
import { ReactComponent as HeartShake } from '../assets/HeartShake.svg';
import { ReactComponent as Signal } from '../assets/signal.svg';
import { ReactComponent as Stethoscope } from '../assets/stethoscope.svg';
import DescriptionIcon from '@mui/icons-material/Description';
import ScienceIcon from '@mui/icons-material/Science';

const linkStyle = {
  padding: "4px 14px",
  borderRadius: "8px",
  textDecoration: "none",
  color: "#444444",
  fontWeight: "500",
  transition: "0.3s",
  
  alignItems:'center'
};

const activeStyle = {
  backgroundColor: "#E5E4E2",
  color: "black",
};

export default function Anatomy() {
  const [edit, setEdit] = useState(true);
  const location = useLocation();
  const { patientId } = useParams();
  const patientReducer = useSelector((state) => state.patientReducer);
  const showToast = useToast();
  const { hasPermission } = usePermissions();
  const isHistoryPage = location.pathname.includes("/history");
  const route = location.pathname.split("/").pop().toLowerCase();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const mode = queryParams.get("mode");
    if (mode) {
      setEdit(mode === "view" ? false : true);
    }
  }, [location.search]);

  const validateNavigation = (e) => {
    // if (!patientReducer.history) {
    //   showToast("Patient requires medical confirmation.", "info");
    //   e.preventDefault();
    //   return;
    // }

    if (!patientReducer?.patientInfo?.lmp) {
      showToast("LMP is mandatory.", "info");
      e.preventDefault();
      return;
    }

    if (!patientReducer?.patientInfo?.conditions?.length) {
      showToast("Please add at least one condition.", "info");
      e.preventDefault();
    }
  };

  const Navigation = (
    <Box
      component="nav"
      sx={{
        display: "flex",
        // justifyContent: { xs: 'flex-start', md: "space-around" },
        gap: { xs: 1, md: 2 },
        // mb: 2,
        width: {
          xs: '100%',
          sm: "90%",
          md: "70%",
          lg: "60%",
          xl: "50%",
        },
        overflowX: { xs: 'auto', md: 'visible' },
        flexWrap: 'nowrap',
        '& a': {
          flexShrink: 0,
          fontSize: { xs: '13px', md: '14px' },
          padding: { xs: '6px 10px', md: '8px 16px' },
          whiteSpace: 'nowrap',
        },
        '&::-webkit-scrollbar': { height: 3 },
      }}
    >
      {(hasPermission("HISTORY_VIEW")) && <NavLink
        to={`history?mode=${edit ? "edit" : "view"}`}
        style={({ isActive }) => ({
          ...linkStyle,
          ...(isActive ? activeStyle : {}),
          justifyItems: 'center', display: 'flex',
        })}
      >
        <HeartShake width={23} height={23} />
        <Typography sx={{ ml: '3px',fontSize: '15px', }}>
          Her Story
        </Typography>
      </NavLink>}
      {(hasPermission("VITALS_VIEW")) && <NavLink
        to={`vitals?mode=${edit ? "edit" : "view"}`}
        style={({ isActive }) => ({
          ...linkStyle,
          ...(isActive ? activeStyle : {}),
          justifyItems: 'center', display: 'flex'
        })}
        onClick={validateNavigation}
      >
        <Signal width={25} height={25} />
        <Typography sx={{ ml: '3px',fontSize: '15px', }}>
          Vitals
        </Typography>
      </NavLink>}
      {(hasPermission("OBSERVATIONS_VIEW")) && <NavLink
        to={`observation?mode=${edit ? "edit" : "view"}`}
        style={({ isActive }) => ({
          ...linkStyle,
          ...(isActive ? activeStyle : {}),
          justifyItems: 'center', display: 'flex'
        })}
        onClick={validateNavigation}
      >
        <Stethoscope />
        <Typography sx={{ ml: '3px',fontSize: '15px', }}>
        Observation
        </Typography>
      </NavLink>}
      {(hasPermission("TEST_VIEW")) && <NavLink
        to={`antenatal?mode=${edit ? "edit" : "view"}`}
        style={({ isActive }) => ({
          ...linkStyle,
          ...(isActive ? activeStyle : {}),
          justifyItems: 'center', display: 'flex'
        })}
        onClick={validateNavigation}
      >
        <ScienceIcon />
        <Typography sx={{ ml: '3px',fontSize: '15px', }}>
        Tests
        </Typography>
      </NavLink>}
      {(hasPermission("TREATMENT_VIEW")) && <NavLink
        to={`treatment?mode=${edit ? "edit" : "view"}`}
        style={({ isActive }) => ({
          ...linkStyle,
          ...(isActive ? activeStyle : {}),
          justifyItems: 'center', display: 'flex'
        })}
        onClick={validateNavigation}
      >
        <DescriptionIcon />
        <Typography sx={{ ml: '3px',fontSize: '15px', }}>
        Treatment
        </Typography>
      </NavLink>}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Navigation on top full width */}
      <Box sx={{ px: 1, py: 1 }}>
        {Navigation}
      </Box>

      {/* Below navigation */}
      <Box sx={{ display: 'flex', flexDirection: 'row', flex: 1, overflow: 'hidden' }}>
        {/* Left side timeline - only show on desktop */}
        <Box sx={{
          width: { xs: '0', md: '200px', lg: '220px', xl: '220px' },
          display: { xs: 'none', md: 'block' },
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 1.5,
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}>
          {route == "history" ? <PatientConditionTimeline patientId={patientId} /> : <PatientInfo patientId={patientId} />}
        </Box>

        {/* Main content */}
        <Box sx={{
          flex: 1,
          px: isHistoryPage ? 1 : 1,
          pb: 1,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
